import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const REACTION_TYPES = {
  // Effect reactions
  applause: { emoji: '👏', label: 'Applause', count: 15 },
  hearts: { emoji: '❤️', label: 'Hearts', count: 12 },
  fire: { emoji: '🔥', label: 'Fire', count: 10 },
  celebrate: { emoji: '🎉', label: 'Celebrate', count: 12 },
  confetti: { emoji: '🎉', label: 'Confetti', count: 20 },
  balloon: { emoji: '🎈', label: 'Balloon', count: 15 },
  mind_blown: { emoji: '🤯', label: 'Mind Blown', count: 8 },
  // Boosted reactions
  rocket: { emoji: '🚀', label: 'Rocket', count: 10 },
  boom: { emoji: '💥', label: 'Boom', count: 12 },
  // Standard reactions (for when triggered)
  clap: { emoji: '👏', label: 'Clap', count: 12 },
  thumbs_up: { emoji: '👍', label: 'Thumbs Up', count: 10 },
  thumbsUp: { emoji: '👍', label: 'Thumbs Up', count: 10 },
  thumbsup: { emoji: '👍', label: 'Thumbs Up', count: 10 },
  thumbs: { emoji: '👍', label: 'Thumbs', count: 10 },
  heart: { emoji: '❤️', label: 'Heart', count: 12 },
  laugh: { emoji: '😂', label: 'Laugh', count: 10 },
  wow: { emoji: '😮', label: 'Wow', count: 8 },
};

// Pre-computed random values passed as props to prevent jittery re-renders
function FloatingReaction({ emoji, delay, startX, initialRotate, targetY, targetXOffset, targetRotate, duration, leftPosition }) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 100,
        x: startX,
        scale: 0.5,
        rotate: initialRotate
      }}
      animate={{
        opacity: [0, 1, 1, 0],
        y: targetY,
        x: startX + targetXOffset,
        scale: [0.5, 1.2, 1, 0.8],
        rotate: targetRotate
      }}
      transition={{
        duration: duration,
        delay,
        ease: "easeOut"
      }}
      className="absolute bottom-20 text-4xl pointer-events-none select-none"
      style={{ left: `${leftPosition}%` }}
    >
      {emoji}
    </motion.div>
  );
}

export default function ReactionsOverlay({ activeReaction, onReactionComplete }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Only trigger if we have an active reaction
    if (!activeReaction) return;

    const reaction = REACTION_TYPES[activeReaction];
    if (!reaction) {
      console.log('Unknown reaction type:', activeReaction);
      onReactionComplete?.();
      return;
    }

    // Create particles with ALL random values pre-computed
    // This prevents jittery animations from random values changing on re-render
    const newParticles = [];
    const timestamp = Date.now();

    for (let i = 0; i < reaction.count; i++) {
      newParticles.push({
        id: `${timestamp}-${i}`,
        emoji: reaction.emoji,
        delay: i * 0.1,
        startX: Math.random() * 100 - 50,
        // Pre-compute all animation random values
        initialRotate: Math.random() * 30 - 15,
        targetY: -400 - Math.random() * 200,
        targetXOffset: Math.random() * 100 - 50,
        targetRotate: Math.random() * 60 - 30,
        duration: 2.5 + Math.random(),
        leftPosition: 20 + Math.random() * 60
      });
    }

    setParticles(newParticles);

    // Clear after animation
    const timeout = setTimeout(() => {
      setParticles([]);
      onReactionComplete?.();
    }, 4000);

    return () => clearTimeout(timeout);
  }, [activeReaction]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[9998] overflow-hidden">
      <AnimatePresence>
        {particles.map((particle) => (
          <FloatingReaction
            key={particle.id}
            emoji={particle.emoji}
            delay={particle.delay}
            startX={particle.startX}
            initialRotate={particle.initialRotate}
            targetY={particle.targetY}
            targetXOffset={particle.targetXOffset}
            targetRotate={particle.targetRotate}
            duration={particle.duration}
            leftPosition={particle.leftPosition}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

export { REACTION_TYPES };
